import * as React from 'react';

import type {
  AudienceDelta,
  CohortAudience,
  Learner,
} from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import { useAudienceLearnerEditor } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearnerEditor';
import type { LearnerListAudience } from 'bundles/enterprise-admin-messaging-v2/hooks/useAudienceLearners';
import { useCohortLearnerEditor } from 'bundles/enterprise-admin-messaging-v2/hooks/useCohortLearnerEditor';
import useLearnerList from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';
import type { LearnerRow } from 'bundles/enterprise-admin-messaging-v2/hooks/useLearnerList';

/**
 * What to persist when the admin saves, discriminated by entry point:
 *  - `filter`: a delta against the server cohort (auto-generate or filter
 *    flow); the BE resolves the final list from the delta.
 *  - `list`: the in-memory working copy (manual flow).
 */
export type LearnerListSaveResult =
  | { kind: 'filter'; delta: AudienceDelta; addedLearners?: Learner[] }
  | { kind: 'list'; learners: Learner[] };

type Params = {
  /** Cohort the editor opens with — the in-memory working copy starts here. */
  initialLearners: Learner[];
  /**
   * Filter-backed entry point: the pool is fetched server-side from this ready
   * `{ orgId, filter }` audience and edits are tracked as a delta. Absent for
   * the manual flow.
   */
  audience?: LearnerListAudience;
  /** NLQ cohort audience for the AI compose flow. */
  cohortAudience?: CohortAudience;
  /** Delta to seed the editor with when re-opening (filter flow). */
  initialDelta?: AudienceDelta;
  /** Learner objects for `initialDelta.addedIds` (cohort flow re-open). */
  initialAddedLearners?: Learner[];
};

type Result = {
  searchQuery: string;
  setSearchQuery: (next: string) => void;
  /** 1-indexed current page. */
  page: number;
  setPage: (next: number) => void;
  pageCount: number;
  pageRows: LearnerRow[];
  /** Total rows matched by the current query (across all pages). */
  filteredCount: number;
  loading: boolean;
  /** True when the (server) page fetch failed. */
  error: boolean;
  /** Re-include / add a learner (by row). */
  addLearner: (learner: Learner) => void;
  /** Remove a learner from the selection (by id). */
  removeLearner: (id: string) => void;
  /** The payload to persist on Save, discriminated by entry point. */
  save: LearnerListSaveResult;
};

/**
 * Single source of truth for *how each entry point* sources, searches, and edits
 * the learner list behind the "Add or remove learners" screen. The screen stays
 * presentational — it renders `pageRows` and calls `addLearner` / `removeLearner`
 * without knowing whether the data is server-fetched or in-memory.
 *
 * Three entry points exist today:
 *  - **Filter:** `audience` present. The cohort is fetched page-by-page from
 *    the gateway and edits are a removals-only delta.
 *  - **Cohort:** `cohortAudience` present. The org pool (INVITED + JOINED) is
 *    fetched via adminuserfilter; cohort membership checked per page in parallel.
 *    Edits are tracked as a bidirectional delta (addedIds + removedIds).
 *  - **Manual:** no audience. The working copy is an in-memory list.
 */
export const useLearnerListEntryPoint = ({
  initialLearners,
  audience,
  cohortAudience,
  initialDelta,
  initialAddedLearners,
}: Params): Result => {
  const isServer = Boolean(audience);
  const isCohort = Boolean(cohortAudience);

  // --- Manual (in-memory) entry point ---
  const [editedList, setEditedList] = React.useState<Learner[]>(initialLearners);
  const clientEditor = useLearnerList({ list: editedList });

  // --- Filter (server-backed) entry point ---
  const serverEditor = useAudienceLearnerEditor({ audience, initialDelta });

  // --- Cohort (NLQ-backed) entry point ---
  // Browses org pool via adminuserfilter; membership checked per page.
  const cohortEditor = useCohortLearnerEditor({ cohortAudience, initialDelta, initialAddedLearners });

  const addLearner = React.useCallback(
    (learner: Learner) => {
      if (isCohort) {
        cohortEditor.addLearner(learner);
      } else if (isServer) {
        serverEditor.addLearner(learner.id);
      } else {
        setEditedList((previous) =>
          previous.some((existing) => existing.id === learner.id) ? previous : [...previous, learner]
        );
      }
    },
    [isCohort, isServer, serverEditor, cohortEditor]
  );

  const removeLearner = React.useCallback(
    (id: string) => {
      if (isCohort) {
        cohortEditor.removeLearner(id);
      } else if (isServer) {
        serverEditor.removeLearner(id);
      } else {
        setEditedList((previous) => previous.filter((learner) => learner.id !== id));
      }
    },
    [isCohort, isServer, serverEditor, cohortEditor]
  );

  // The active source supplies the common list/search/pagination surface.
  if (isCohort) {
    const save: LearnerListSaveResult = {
      kind: 'filter',
      delta: cohortEditor.delta,
      addedLearners: cohortEditor.addedLearners,
    };
    return {
      searchQuery: cohortEditor.searchQuery,
      setSearchQuery: cohortEditor.setSearchQuery,
      page: cohortEditor.page,
      setPage: cohortEditor.setPage,
      pageCount: cohortEditor.pageCount,
      pageRows: cohortEditor.pageRows,
      filteredCount: cohortEditor.filteredCount,
      loading: cohortEditor.loading,
      error: cohortEditor.error,
      addLearner,
      removeLearner,
      save,
    };
  }

  const active = isServer ? serverEditor : clientEditor;

  const save: LearnerListSaveResult = isServer
    ? { kind: 'filter', delta: serverEditor.delta }
    : { kind: 'list', learners: editedList };

  return {
    searchQuery: active.searchQuery,
    setSearchQuery: active.setSearchQuery,
    page: active.page,
    setPage: active.setPage,
    pageCount: active.pageCount,
    pageRows: active.pageRows,
    filteredCount: active.filteredCount,
    loading: isServer ? serverEditor.loading : false,
    error: isServer ? serverEditor.error : false,
    addLearner,
    removeLearner,
    save,
  };
};

export default useLearnerListEntryPoint;
